import { IconAction } from "@common/controls/Icon/component";
import withDropdownHOC, {
  type WithDropdownCmpProps,
  type WithDropdownComponentAddedProps,
  type WithDropdownToggleProps
} from "@common/hocs/withDropdown";
import { Common } from "@common/types";
import classNames from "classnames";
import React from "react";

import "./style.less";

export type MenuContentProps<T> = {
  close: () => void;
  item: T;
};

export type MenuProps<T, TMenuProps> = {
  className?: string;
  isDisabled?: boolean;
  item: T;
  Content?: React.ComponentType<MenuContentProps<T>>;
  contentProps?: TMenuProps;
  name: string;
  noArrow?: boolean;
  theme?: "black" | "white";
  toggleIcon?: string;
  toggleTooltip?: string;
};

const MenuToggle = <T extends object, TMenuProps = {}>(props: WithDropdownToggleProps & MenuProps<T, TMenuProps>) => (
  <div
    className={classNames("menu toggle", props.className, {
      open: props.isOpen,
      "not-allowed": props.isDisabled
    })}
    data-name={props.name}
    onClick={(e) => {
      if (!props.isDisabled) {
        e.stopPropagation();
        props.toggleOpen();
      }
    }}
    ref={props.toggleRef}
  >
    <IconAction tooltip={props.toggleTooltip} icon={props.toggleIcon ?? Common.IconType.EllipsisVertical} size={20} />
  </div>
);

const MenuContent = React.forwardRef(
  <T extends object, TMenuProps = {}>(
    props: WithDropdownCmpProps & MenuProps<T, TMenuProps>,
    ref: React.RefObject<any>
  ) => {
    const Menu = props.Content!;
    return (
      <div ref={ref} className="controls">
        <Menu {...(props.contentProps || {})} close={props.close} item={props.item} />
      </div>
    );
  }
);

// menu is rendered outside modals. If a menu contains a modal the ignoreSelector will prevent it from closing on click
const MenuDropdownBase = withDropdownHOC<MenuProps<any, any>>(MenuToggle, MenuContent, {
  scrollBehaviour: "close"
});
const Menu = <T extends object, TMenuProps = {}>({
  dropdownClassName,
  ...props
}: MenuProps<T, TMenuProps> & WithDropdownComponentAddedProps) => (
  <MenuDropdownBase
    {...props}
    dropdownClassName={classNames(dropdownClassName, "menu", props.theme || "black", { no_arrow: props.noArrow })}
    maxHeight="unset"
  />
);
export default Menu;
